import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Package, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  codigo: '', nombre: '', descripcion: '',
  precio_unitario: 0, iva_porcentaje: 21, unidad: 'ud',
  tipo: 'servicio', categoria: '', linked_course_id: '', activo: true
};

const Products = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('own'); // 'own' | 'courses'
  const [items, setItems] = useState([]);
  const [courseProducts, setCourseProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const canEdit = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/billing/products`),
        axios.get(`${API_URL}/billing/products/from-courses`),
      ]);
      setItems(r1.data);
      setCourseProducts(r2.data);
    } catch (e) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyForm, ...p }); setShowDialog(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.precio_unitario = parseFloat(payload.precio_unitario || 0);
      payload.iva_porcentaje = parseFloat(payload.iva_porcentaje || 0);

      if (editing) {
        await axios.put(`${API_URL}/billing/products/${editing.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API_URL}/billing/products`, payload);
        toast.success('Producto creado');
      }
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await axios.delete(`${API_URL}/billing/products/${id}`);
      toast.success('Producto eliminado');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const list = tab === 'own' ? items : courseProducts;
  const filtered = list.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.nombre?.toLowerCase().includes(s) ||
           p.codigo?.toLowerCase().includes(s) ||
           p.descripcion?.toLowerCase().includes(s);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="products-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Productos y Servicios</h1>
            <p className="text-slate-500 mt-1">Catálogo facturable. Puedes crear productos propios o reutilizar los cursos del LMS.</p>
          </div>
          {canEdit && tab === 'own' && (
            <Button data-testid="new-product-button" onClick={openCreate} className="bg-[#004E89] hover:bg-[#003a68]">
              <Plus className="w-4 h-4 mr-2" /> Nuevo producto
            </Button>
          )}
        </div>

        <div className="flex border-b border-slate-200">
          <button data-testid="products-tab-own" onClick={()=>setTab('own')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab==='own' ? 'border-[#004E89] text-[#004E89]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Package className="w-4 h-4 inline mr-1" /> Productos propios ({items.length})
          </button>
          <button data-testid="products-tab-courses" onClick={()=>setTab('courses')} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab==='courses' ? 'border-[#004E89] text-[#004E89]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <GraduationCap className="w-4 h-4 inline mr-1" /> Cursos del LMS ({courseProducts.length})
          </button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              {tab === 'own' ? (search ? 'No hay resultados.' : 'Todavía no has creado productos propios.') : 'No hay cursos disponibles.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Código</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nombre</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Tipo</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Unidad</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Precio</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">IVA</th>
                    <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 text-sm text-slate-600 font-mono">{p.codigo || '-'}</td>
                      <td className="p-3 text-sm font-medium text-slate-900">
                        {p.nombre}
                        {p.descripcion && <div className="text-xs text-slate-500 truncate max-w-md">{p.descripcion.replace(/<[^>]+>/g,'').slice(0, 90)}</div>}
                      </td>
                      <td className="p-3 text-sm text-slate-600 capitalize">{p.tipo}</td>
                      <td className="p-3 text-sm text-slate-600">{p.unidad}</td>
                      <td className="p-3 text-sm text-right text-slate-900">{(p.precio_unitario || 0).toFixed(2)} €</td>
                      <td className="p-3 text-sm text-right text-slate-600">{p.iva_porcentaje}%</td>
                      <td className="p-3 text-right space-x-2">
                        {tab === 'own' && canEdit && (
                          <>
                            <button onClick={()=>openEdit(p)} className="text-slate-600 hover:text-blue-600"><Edit className="w-4 h-4 inline" /></button>
                            <button onClick={()=>handleDelete(p.id)} className="text-slate-600 hover:text-red-600"><Trash2 className="w-4 h-4 inline" /></button>
                          </>
                        )}
                        {tab === 'courses' && (
                          <span className="text-xs text-slate-400">Editar en el LMS</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar producto' : 'Nuevo producto'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Código / Referencia</Label>
                  <Input value={form.codigo} onChange={e=>set('codigo', e.target.value)} placeholder="opcional" />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <select className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" value={form.tipo} onChange={e=>set('tipo', e.target.value)}>
                    <option value="servicio">Servicio</option>
                    <option value="producto">Producto</option>
                    <option value="curso">Curso</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>Nombre *</Label>
                  <Input required value={form.nombre} onChange={e=>set('nombre', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Descripción</Label>
                  <textarea className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" rows={3} value={form.descripcion} onChange={e=>set('descripcion', e.target.value)} />
                </div>
                <div>
                  <Label>Precio unitario (€) *</Label>
                  <Input required type="number" step="0.01" value={form.precio_unitario} onChange={e=>set('precio_unitario', e.target.value)} />
                </div>
                <div>
                  <Label>IVA (%) *</Label>
                  <select className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" value={form.iva_porcentaje} onChange={e=>set('iva_porcentaje', e.target.value)}>
                    <option value={0}>0% (Exento)</option>
                    <option value={4}>4% (Superreducido)</option>
                    <option value={10}>10% (Reducido)</option>
                    <option value={21}>21% (General)</option>
                  </select>
                </div>
                <div>
                  <Label>Unidad</Label>
                  <Input value={form.unidad} onChange={e=>set('unidad', e.target.value)} placeholder="ud, h, servicio..." />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Input value={form.categoria} onChange={e=>set('categoria', e.target.value)} />
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <input type="checkbox" id="activo" checked={form.activo} onChange={e=>set('activo', e.target.checked)} />
                  <label htmlFor="activo" className="text-sm">Activo (disponible para facturar)</label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={()=>setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#004E89] hover:bg-[#003a68]">{editing ? 'Guardar' : 'Crear'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Products;
